
import { useState, useRef } from 'react';
import { __ } from '@wordpress/i18n';
import { InspectorControls } from '@wordpress/block-editor';
import { PanelBody, PanelRow, TabPanel, ToggleControl, SelectControl, RangeControl, __experimentalUnitControl as UnitControl, __experimentalNumberControl as NumberControl, __experimentalInputControl as InputControl, __experimentalBoxControl as BoxControl, } from '@wordpress/components';
import produce from 'immer';

// Settings Components
import { Label, BColor, BDevice, Typography, HelpPanel, BorderControl } from '../../Components';

import { tabController } from '../../Components/utils/functions';
import { emUnit, perUnit, pxUnit } from '../../Components/utils/options';

import { generalStyleTabs, layouts, orderByOpt, resourceOpt } from './utils/options';
import { debounce, checkLayout } from './utils/functions';
import { useEffect } from '@wordpress/element';

const Settings = ({ attributes, setAttributes, setNextPageToken }) => {
	const { columns, columnGap, rowGap, configure, player, img, title, desc, layout, titleTypo, titleColor, descTypo, descColor, info, boxPadding, boxBorder } = attributes;

	const { bRadius } = img;
	const { height, controls, mute, rel, autoPlay, modestBranding, fs } = player;
	const { resource, orderBy } = configure;

	const [device, setDevice] = useState('desktop');
	const [localConfig, setLocalConfig] = useState(configure);

	const updateObject = (attr, key, val) => {
		const newAttr = { ...attributes[attr] };
		newAttr[key] = val;
		setAttributes({ [attr]: newAttr })
	}
	const titleShow = title?.show;
	const descShow = desc?.show;

	const updateConfigure = useRef(debounce((prevConfig, key, val) => {
		setAttributes({ configure: { ...prevConfig, [key]: val } });
	}, 750)).current;

	const updateConfig = (prevConfig, key, val) => {
		setLocalConfig({ ...localConfig, [key]: val });
		updateConfigure(prevConfig, key, val);
	}

	useEffect(() => {
		setLocalConfig({ ...localConfig, id: configure.id })
	}, [configure])

	return <>
		<InspectorControls>
			<TabPanel className='bPlTabPanel' activeClass='activeTab' tabs={generalStyleTabs} onSelect={tabController}>{tab => <>
				{'general' === tab.name && <>
					<HelpPanel slug="video-gallery-for-youtube" docsLink="https://bplugins.com/docs/video-gallery-for-youtube" />

					<PanelBody className='bPlPanelBody ytvgbbPlPanelBody' title={__('Configure', 'yt-video-gallery')}>
						<button className='ytvgbTopBtn' onClick={() => {
							wp.data.dispatch('core/edit-post').openGeneralSidebar('ytvgb-gallery/youTube-video-gallery');
						}}>Configure Api Key</button>
						<PanelRow className='ytvgbPanelRow'>
							<SelectControl className='ytvgbInputControl mt15' label={__('Resource to use', 'yt-video-gallery')} value={resource} options={resourceOpt} onChange={val => {
								setAttributes({
									configure: {
										...configure,
										resource: val,
										id: null
									}
								});
								setNextPageToken('');
							}} />

							<SelectControl className='ytvgbInputControl mt15' label={__('Order By', 'yt-video-gallery')} value={orderBy} options={orderByOpt} onChange={(val) => { updateObject('configure', 'orderBy', val); }} />
						</PanelRow>

						<InputControl label={__('Resource ID', 'yt-video-gallery')} className="ytvgbInputControl mt15" value={localConfig?.id} onChange={val => updateConfig(configure, 'id', val)} />
						<div className='mainSection'>
							<h2>Help</h2>
							{
								resource === "channel" ? <div className='actionBtn'>
									<a className='ytvgbActionHelp' href='https://bplugins.com/docs/video-gallery-for-youtube/guides/credentials/#channel' target='__blank'>Official</a>
									<a className='ytvgbActionHelp' href='https://commentpicker.com/youtube-channel-id.php' target='__blank'>Unofficial</a></div>
									: <div className='mainAction'>  <a className='ytvgbActionHelp' href='https://bplugins.com/docs/video-gallery-for-youtube/guides/credentials/#playlist' target='__blank'>Official</a></div>
							}</div>
						<Label className="mt15"> {__('Total Videos', 'yt-video-gallery')} </Label>
						<RangeControl value={localConfig?.perPage} onChange={(val) => {
							updateConfig(configure, 'perPage', val);
						}} min={1} max={50} />

					</PanelBody>

					<PanelBody className='bPlPanelBody' title={__('Layout', 'yt-video-gallery')} initialOpen={false}>
						<SelectControl labelPosition="side" label={__('Layout', 'yt-video-gallery')} value={layout} options={layouts} onChange={(val) => { setAttributes({ layout: val, ...checkLayout(val) }) }} />

						{layout !== 'playlist' && <>
							<PanelRow>
								<Label mt='0'>{__('Columns:', 'yt-video-gallery')}</Label>
								<BDevice device={device} onChange={val => setDevice(val)} />
							</PanelRow>

							<RangeControl value={columns[device]} onChange={val => { setAttributes({ columns: { ...columns, [device]: val } }) }} min={1} max={6} step={1} beforeIcon='grid-view' />

							<UnitControl className='mt20' label={__('Column Gap:', 'yt-video-gallery')} labelPosition='left' value={columnGap} onChange={val => setAttributes({ columnGap: val })} units={[pxUnit(30), perUnit(3), emUnit(2)]} isResetValueOnUnitChange={true} />

							<UnitControl className='mt20' label={__('Row Gap:', 'yt-video-gallery')} labelPosition='left' value={rowGap} onChange={val => setAttributes({ rowGap: val })} units={[pxUnit(40), perUnit(3), emUnit(2.5)]} isResetValueOnUnitChange={true} />
						</>}
					</PanelBody>

					<PanelBody className='bPlPanelBody' title={__('Video Player', 'yt-video-gallery')} initialOpen={false}>
						<NumberControl labelPosition="side" className="ytvgbInputControl mt15" label={__('Height', 'yt-video-gallery')} value={height} onChange={(val) => { updateObject('player', 'height', val); }} />

						<ToggleControl className='mt10' label={__('Show Player Controls', 'yt-video-gallery')} labelPosition='side' checked={controls} onChange={(val) => { updateObject('player', 'controls', val); }} />

						{layout === 'playlist' && <>
							<ToggleControl className='mt10' label={__('Mute', 'yt-video-gallery')} labelPosition='side' checked={mute} onChange={(val) => { updateObject('player', 'mute', val); }} />

							<ToggleControl className='mt10' label={__('Autoplay video', 'yt-video-gallery')} labelPosition='side' checked={autoPlay} onChange={(val) => { updateObject('player', 'autoPlay', val); }} />
						</>}

						<ToggleControl className='mt10' label={__('Suggest Only Channel videos After Played', 'yt-video-gallery')} labelPosition='side' checked={rel} onChange={(val) => { updateObject('player', 'rel', val); }} />

						<ToggleControl className='mt10' label={__('Show Video Logo', 'yt-video-gallery')} labelPosition='side' checked={modestBranding} onChange={(val) => { updateObject('player', 'modestBranding', val); }} />

						<ToggleControl className='mt10' label={__('Show FullScreen Icon', 'yt-video-gallery')} labelPosition='side' checked={fs} onChange={(val) => { updateObject('player', 'fs', val); }} />

						{/* <ToggleControl className='mt10' label={__('Hide Annotation', 'yt-video-gallery')} labelPosition='side' checked={annotation} onChange={(val) => { updateObject('player', 'annotation', val); }} /> */}
					</PanelBody>

					<PanelBody className='bPlPanelBody' title={__('Elements', 'yt-video-gallery')} initialOpen={false}>
						<ToggleControl className='mt10' label={__('Show Title', 'yt-video-gallery')} labelPosition='side' checked={title?.show} onChange={(val) => { updateObject('title', 'show', val); }} />

						{['grid-details', 'playlist'].includes(layout) && <>
							<ToggleControl className='mt10' label={__('Show Description', 'yt-video-gallery')} labelPosition='side' checked={desc?.show} onChange={(val) => { updateObject('desc', 'show', val) }} />
						</>}

						{['grid-details', 'default'].includes(layout) && <>
							<ToggleControl className='mt10' label={__('Show View', 'yt-video-gallery')} labelPosition='side' checked={info?.isView} onChange={(val) => { updateObject('info', 'isView', val) }} />

							<ToggleControl className='mt10' label={__('Show Like', 'yt-video-gallery')} labelPosition='side' checked={info?.isLike} onChange={(val) => { updateObject('info', 'isLike', val) }} />

							<ToggleControl className='mt10' label={__('Show Comment', 'yt-video-gallery')} labelPosition='side' checked={info?.isComment} onChange={(val) => { updateObject('info', 'isComment', val) }} />
						</>}

						{'default' === layout && <ToggleControl className='mt10' label={__('Show Date', 'yt-video-gallery')} labelPosition='side' checked={info?.isPDate} onChange={(val) => { updateObject('info', 'isPDate', val) }} />}
					</PanelBody>

					{titleShow && <>
						<PanelBody className='bPlPanelBody' title={__('Title', 'yt-video-gallery')} initialOpen={false}>
							<NumberControl labelPosition="side" className="ytvgbInputControl mt15" label={__('Title length', 'yt-video-gallery')} value={title?.limit} onChange={(val) => { updateObject('title', 'limit', val); }} />

							<ToggleControl className='mt15' label={__('Open link in new a tab', 'yt-video-gallery')} checked={title?.target} onChange={(val) => { updateObject('title', 'target', val); }} />
						</PanelBody>
					</>}

					{descShow && <>
						{['grid-details', 'playlist'].includes(layout) && <>
							<PanelBody className='bPlPanelBody' title={__('Description', 'yt-video-gallery')} initialOpen={false}>
								<NumberControl labelPosition="side" className="ytvgbInputControl mt15" label={__('Length', 'yt-video-gallery')} value={desc?.limit} onChange={(val) => { updateObject('desc', 'limit', val) }} />
							</PanelBody>
						</>}
					</>}

				</>}

				{'style' === tab.name && <>

					<PanelBody className='bPlPanelBody' title={__('Box', 'yt-video-gallery')} initialOpen={false}>

						<BoxControl label={__('Padding', 'b-testimonials-block')} values={boxPadding} onChange={val => setAttributes({ boxPadding: val })} resetValues={{ top: "10px", right: "10px", bottom: "10px", left: "10px" }} units={[pxUnit(3), emUnit(2)]} />

						<BorderControl label={__('Border', 'yt-video-gallery')} value={boxBorder} onChange={(val) => setAttributes({ boxBorder: val })} />

					</PanelBody>

					<PanelBody className='bPlPanelBody' title={__('Image', 'yt-video-gallery')} initialOpen={false}>
						<UnitControl className='mt10' label={__('Radius:', 'business-review')} labelPosition='left' value={bRadius} onChange={val => setAttributes({ img: { ...img, bRadius: val } })} units={[pxUnit(10), perUnit(100)]} isResetValueOnUnitChange={true} />
					</PanelBody>

					{titleShow && <>
						<PanelBody className='bPlPanelBody' title={__('Title', 'yt-video-gallery')} initialOpen={false}>
							<Typography className='' label={__('Typography', 'yt-video-gallery')} value={titleTypo} onChange={(val) => { setAttributes({ titleTypo: val }) }} produce={produce} />

							<BColor label={__('Color', 'yt-video-gallery')} className='' value={titleColor} onChange={(val) => { setAttributes({ titleColor: val }) }} />
						</PanelBody>
					</>}

					{((layout === 'grid-details' || layout === 'playlist') && descShow) && <>
						<PanelBody className='bPlPanelBody' title={__('Description', 'yt-video-gallery')} initialOpen={false}>
							<Typography className='' label={__('Typography', 'yt-video-gallery')} value={descTypo} onChange={(val) => { setAttributes({ descTypo: val }) }} produce={produce} />

							<BColor label={__('Color', 'yt-video-gallery')} className='' value={descColor} onChange={(val) => { setAttributes({ descColor: val }) }} />
						</PanelBody>
					</>}

					{['grid-details', 'default'].includes(layout) &&
						<PanelBody className='bPlPanelBody' title={__('Video Info', 'yt-video-gallery')} initialOpen={false}>
							<RangeControl className='mt20' label={__('Size', 'yt-video-gallery')} value={info?.numberSize} onChange={(val) => setAttributes({ info: { ...info, numberSize: val, iconSize: val } })} min={1} max={100} />

							<BColor className='mt20' label={__('Color', 'yt-video-gallery')} value={info?.numberColor} onChange={val => setAttributes({ info: { ...info, numberColor: val, iconColor: val } })} />
						</PanelBody>}
				</>}
			</>}</TabPanel>
		</InspectorControls>
	</>;
};
export default Settings;

export const defaultValue = (val, configure, setAttributes) => {
	setAttributes({
		configure: {
			...configure,
			resource: val,
			id: null
		}
	});
};